import { motion } from 'framer-motion';
import { Home, Building2, Sparkles, HardHat, PackageOpen } from 'lucide-react';

const SERVICES = [
  {
    id: 'residential',
    icon: Home,
    title: 'Residential Cleaning',
    desc: 'Recurring or one-time home cleaning — kitchens, bathrooms, bedrooms and living spaces, detailed every visit.',
    img: 'https://images.pexels.com/photos/29412579/pexels-photo-29412579.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    tag: 'Homes & apartments',
    span: 'lg:col-span-7',
    height: 'h-[400px] lg:h-[460px]',
  },
  {
    id: 'commercial',
    icon: Building2,
    title: 'Commercial Cleaning',
    desc: 'Offices, restaurants and retail spaces kept fresh, hygienic and customer-ready — including evenings and overnights.',
    img: 'https://images.unsplash.com/photo-1781637590564-01c65dbf2039?crop=entropy&cs=srgb&fm=jpg&q=85&w=900',
    tag: 'Office · Restaurant · Retail',
    span: 'lg:col-span-5',
    height: 'h-[400px] lg:h-[460px]',
  },
  {
    id: 'deep-cleaning',
    icon: Sparkles,
    title: 'Deep Cleaning',
    desc: 'A top-to-bottom detail clean for spaces that need extra love — appliances, tile, grout, baseboards and more.',
    img: 'https://images.pexels.com/photos/3847549/pexels-photo-3847549.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    tag: 'Seasonal or one-time',
    span: 'lg:col-span-4',
    height: 'h-[360px] lg:h-[400px]',
  },
  {
    id: 'post-construction',
    icon: HardHat,
    title: 'Post-Construction Cleaning',
    desc: 'Dust, debris and paint residue removed so your finished project is ready to enjoy.',
    img: 'https://images.pexels.com/photos/3616746/pexels-photo-3616746.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    tag: 'Renovations & builds',
    span: 'lg:col-span-4',
    height: 'h-[360px] lg:h-[400px]',
  },
  {
    id: 'move-in-out',
    icon: PackageOpen,
    title: 'Move-In / Move-Out Cleaning',
    desc: 'Deposit-safe deep cleans that reset a property to day-one condition.',
    img: 'https://images.unsplash.com/photo-1650347325235-2911d70b1ffc?crop=entropy&cs=srgb&fm=jpg&q=85&w=900',
    tag: 'Renters & landlords',
    span: 'lg:col-span-4',
    height: 'h-[360px] lg:h-[400px]',
  },
];

export default function Services() {
  return (
    <section id="services" data-testid="services-section" className="relative bg-ceramic py-24 lg:py-36">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-16">
          <div>
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.6 }}
              className="font-mono text-xs uppercase tracking-[0.3em] text-mint mb-5"
            >
              01 — Services
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tight text-obsidian leading-[1.05]"
            >
              Every space we clean,
              <br />
              treated like our own.
            </motion.h2>
          </div>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="text-stone-500 text-base lg:text-lg max-w-md leading-relaxed"
          >
            Five services, one obsessive standard — for homes and businesses across
            Metro Vancouver.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {SERVICES.map((s, i) => (
            <motion.a
              key={s.id}
              href="#quote"
              data-testid={`service-card-${s.id}`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.8, delay: (i % 3) * 0.1, ease: [0.16, 1, 0.3, 1] }}
              className={`group relative rounded-3xl overflow-hidden ${s.span} ${s.height} block`}
            >
              <img
                src={s.img}
                alt={s.title}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-obsidian/90 via-obsidian/35 to-obsidian/5 transition-opacity duration-500 group-hover:from-obsidian/95" />
              <div className="relative h-full flex flex-col justify-end p-7 lg:p-9">
                <div className="absolute top-6 left-6 w-11 h-11 rounded-full bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center">
                  <s.icon size={18} className="text-mint-vibrant" />
                </div>
                <span className="absolute top-6 right-6 font-mono text-[10px] uppercase tracking-[0.15em] bg-mint text-white px-3 py-1.5 rounded-full">
                  {s.tag}
                </span>
                <h3 className="font-display font-bold text-white text-2xl lg:text-3xl tracking-tight">
                  {s.title}
                </h3>
                <p className="text-stone-300 text-sm lg:text-base mt-2 max-w-md leading-relaxed">
                  {s.desc}
                </p>
                <div className="mt-4 flex items-center gap-2 text-[13px] text-mint-vibrant font-medium max-h-0 opacity-0 group-hover:max-h-10 group-hover:opacity-100 transition-all duration-500">
                  <span className="w-6 h-px bg-mint-vibrant" />
                  Get a free quote for this service
                </div>
              </div>
            </motion.a>
          ))}
        </div>
      </div>
    </section>
  );
}
